/*
 * Rutele magazinului, in ordinea din contract.
 *
 * Fiecare functie de aici corespunde unei rute montate in
 * `app/config/encrypted_routes/ShopRoutes.php`. Nu se inventeaza rute: un ecran
 * care are nevoie de ceva ce nu exista se noteaza in README, la "Ce lipseste
 * din api". O cale ghicita nu da eroare la compilare - da 404 la client, in
 * demo, si arata ca un api stricat, nu ca o functie lipsa.
 *
 * `PUT /shop/catalog/{id}/steps` exista in contract, dar nu si aici: e ruta de
 * administrare a definitiei, iar catalogul asta nu administreaza nimic.
 */

import { http, refreshTransport, type QueryValue, type RequestOptions } from './client';
import { config } from './config';
import * as session from './session';
import type {
  AuthTokens,
  Cart,
  CatalogItem,
  CatalogItemResponse,
  CategoriesResponse,
  CreateTicketResponse,
  DirectTicketResponse,
  FeedRow,
  HelpArticleResponse,
  HelpListResponse,
  MeResponse,
  Paginated,
  RequestDetailResponse,
  SearchOutcome,
  SearchResponse,
  SsoProvidersResponse,
  SubmitCartResponse,
  TicketTypesResponse,
} from './types';

/** Optiuni pe care ecranele le pot da mai departe: doar semnalul de anulare. */
type Scoped = Pick<RequestOptions, 'signal'>;

function withQuery(scoped: Scoped | undefined, query: Record<string, QueryValue>): RequestOptions {
  return { ...scoped, query };
}

/* ------------------------------------------------------ 1. autentificare */

export function login(username: string, password: string): Promise<AuthTokens> {
  return http.post<AuthTokens>('/shop/auth/login', {
    auth: false,
    /* Cookie-ul httpOnly cu refresh tokenul (daca api-ul il pune) trebuie primit
       chiar de aici, deci cererea trece cu credentiale cand se cere cookie. */
    withCookies: refreshTransport().refresh_transport === 'cookie',
    body: { username, password, ...refreshTransport() },
  });
}

export function logout(): Promise<unknown> {
  /* allowRefresh:false - o iesire din cont care declanseaza un refresh ca sa
     poata invalida acelasi refresh e o contradictie. Contractul spune oricum ca
     ruta reuseste si cand tokenul era deja mort. */
  /* Refresh tokenul pleaca si el, ca api-ul sa-l stearga (ShopAuth::logout il
     citeste din corp). Pana acum corpul era gol, deci refresh tokenul ramanea
     bun si dupa "Sign out" - cine il copiase intra mai departe. Prin cookie, il
     duce browserul, iar api-ul sterge si cookie-ul. */
  const viaCookie = session.usesRefreshCookie();
  const refreshToken = session.getRefreshToken();
  return http.post('/shop/auth/logout', {
    body: !viaCookie && refreshToken !== null ? { refresh_token: refreshToken } : {},
    allowRefresh: false,
    withCookies: viaCookie,
  });
}

export function me(scoped?: Scoped): Promise<MeResponse> {
  return http.get<MeResponse>('/shop/auth/me', scoped);
}

export function ssoProviders(scoped?: Scoped): Promise<SsoProvidersResponse> {
  return http.get<SsoProvidersResponse>('/shop/auth/sso/providers', { ...scoped, auth: false });
}

/**
 * `codeVerifier` e secretul din auth/ssoBinding.ts. Lipsa lui (bilet pornit din
 * alta fila, browser fara WebCrypto) nu opreste schimbul aici: decide api-ul.
 */
export function ssoExchange(ticket: string, codeVerifier: string | null = null): Promise<AuthTokens> {
  return http.post<AuthTokens>('/shop/auth/sso/exchange', {
    auth: false,
    withCookies: refreshTransport().refresh_transport === 'cookie',
    body: {
      ticket,
      ...(codeVerifier !== null ? { code_verifier: codeVerifier } : {}),
      ...refreshTransport(),
    },
  });
}

/* ------------------------------------------------------------ 2. catalog */

export interface CatalogQuery {
  q?: string;
  category_id?: string;
  page?: number;
  per_page?: number;
}

export function catalog(query: CatalogQuery, scoped?: Scoped): Promise<Paginated<CatalogItem>> {
  return http.get<Paginated<CatalogItem>>('/shop/catalog', withQuery(scoped, { ...query }));
}

export function categories(scoped?: Scoped): Promise<CategoriesResponse> {
  return http.get<CategoriesResponse>('/shop/catalog/categories', scoped);
}

export function catalogItem(id: string | number, scoped?: Scoped): Promise<CatalogItemResponse> {
  return http.get<CatalogItemResponse>(`/shop/catalog/${encodeURIComponent(id)}`, scoped);
}

/* ------------------------------------------------------ 3. cos si cereri */

export function cart(scoped?: Scoped): Promise<Cart> {
  return http.get<Cart>('/shop/cart', scoped);
}

export function addToCart(catalogId: number, quantity: number, note: string): Promise<unknown> {
  return http.post('/shop/cart/items', {
    body: { catalog_id: catalogId, quantity, note },
  });
}

export function removeCartItem(itemId: number): Promise<unknown> {
  return http.delete(`/shop/cart/items/${encodeURIComponent(itemId)}`);
}

/**
 * Alta cantitate pe un rand din cos.
 *
 * Nu prin stergere si adaugare la loc: randul si-ar pierde nota si s-ar muta la
 * coada listei. Serverul intoarce cosul intreg, fiindca se schimba si totalul.
 */
export function setCartItemQuantity(itemId: number, quantity: number): Promise<Cart> {
  return http.post<Cart>(`/shop/cart/items/${encodeURIComponent(itemId)}/quantity`, {
    body: { quantity },
  });
}

export function submitCart(note: string, ticketPriority?: number): Promise<SubmitCartResponse> {
  return http.post<SubmitCartResponse>('/shop/cart/submit', {
    /* Trimisa doar cand omul a ales ceva: fara ea serverul pune prioritatea
       implicita a furnizorului, care e purtarea de pana acum. */
    body: ticketPriority ? { note, ticket_priority: ticketPriority } : { note },
  });
}

export interface RequestsQuery {
  state?: string;
  page?: number;
  per_page?: number;
}

export function directTicket(
  id: string | number,
  scoped?: Scoped,
): Promise<DirectTicketResponse> {
  return http.get<DirectTicketResponse>(`/shop/tickets/${encodeURIComponent(id)}`, scoped);
}

/**
 * Continutul unui fisier de pe tichet, adus ca sa poata fi ARATAT.
 *
 * ---------------------------------------------------------------------------
 * DE CE NU E DOAR UN `src` PE O ADRESA
 * ---------------------------------------------------------------------------
 *
 * Autentificarea e prin antet - `Authorization: Bearer` - nu prin cookie. Un
 * `<img src="...">` pleaca fara antete proprii, deci ar fi primit 401 si ar fi
 * aratat o poza rupta. Fisierul se cere deci de aici, cu tokenul, si se preda
 * ecranului ca `blob:`.
 *
 * ---------------------------------------------------------------------------
 * DE CE NU TRECE PRIN `http`
 * ---------------------------------------------------------------------------
 *
 * `client.ts` citeste raspunsul ca JSON si stie sa reia o singura data dupa un
 * refresh de token. Aici vin octeti, nu JSON, iar ca sa treaca prin el ar fi
 * trebuit umblat chiar in bucata care tine reluarea - pentru o previzualizare.
 *
 * Pretul, spus pe fata: daca tokenul a expirat chiar in clipa aia, poza nu se
 * incarca si scrie ca nu s-a putut. Nu se pierde nimic - fisierul e tot acolo,
 * si o reincarcare a paginii il aduce.
 *
 * Cine cheama e dator sa cheme `URL.revokeObjectURL` cand nu-i mai trebuie:
 * altfel fila tine in memorie fiecare poza deschisa pana la inchidere.
 */
export async function ticketFileBlobUrl(
  ticketId: string | number,
  fileId: string | number,
  options: { thumb?: boolean; signal?: AbortSignal } = {},
): Promise<string> {
  const token = session.getToken();

  if (token === null) throw new Error('unauthenticated');

  const url = config.apiBaseUrl
    + `/shop/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(fileId)}/content`
    + (options.thumb ? '?thumb=1' : '');

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
    cache: 'no-store',
    /* `exactOptionalPropertyTypes` e pornit: un `signal: undefined` scris pe
       fata nu e acelasi lucru cu lipsa lui. Se pune numai cand exista - aceeasi
       forma ca in `client.ts`, la corpul cererii. */
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });

  if (!response.ok) throw new Error(String(response.status));

  return URL.createObjectURL(await response.blob());
}

/**
 * Raspunsul omului pe tichetul lui.
 *
 * Nu intoarce comentariul scris: api-ul raspunde `201` fara corp, iar ecranul
 * reincarca fisa. Un comentariu lipit local ar fi aratat pe ecran ceva ce inca
 * nu se stie ca e in baza - si daca scrierea ar fi picat pe drum, omul ar fi
 * plecat convins ca a raspuns.
 */
export function addTicketComment(id: string | number, comment: string): Promise<unknown> {
  return http.post(`/shop/tickets/${encodeURIComponent(id)}/comments`, { body: { comment } });
}

/**
 * Fisiere pe un tichet care exista deja.
 *
 * Nu se trimit odata cu textul: un raspuns scris care ar pica din cauza unui
 * fisier prea mare ar pierde si textul. Asa, ce a reusit ramane.
 *
 * Se trimit toate intr-o singura cerere, nu cate una: api-ul numara cate au
 * voie deodata, iar cinci cereri separate ar fi trecut fiecare de numaratoare.
 */
export function addTicketFiles(id: string | number, files: File[]): Promise<unknown> {
  const form = new FormData();

  /* `files[]`, cu paranteze: asa le aduna PHP intr-un tablou. Fara ele,
     fiecare fisier l-ar suprascrie pe cel dinainte si ar ajunge unul singur. */
  for (const file of files) form.append('files[]', file);

  return http.post(`/shop/tickets/${encodeURIComponent(id)}/attachments`, { form });
}

/**
 * „Cererile mele": cererile de magazin SI tichetele deschise direct.
 *
 * Randurile se deosebesc dupa `kind` - vezi `isDirectTicket` in types.
 */
export function requests(
  query: RequestsQuery,
  scoped?: Scoped,
): Promise<Paginated<FeedRow>> {
  return http.get<Paginated<FeedRow>>('/shop/requests', withQuery(scoped, { ...query }));
}

/*
 * Toate randurile din „Cererile mele", pagina dupa pagina.
 *
 * Ruta nu filtreaza dupa stare - primeste `state`, dar nu-l citeste - iar
 * starea pe care o vede omul vine din tichet, nu din cerere. Deci filtrul si
 * numarul de cereri deschise se fac aici, peste toata lista. Cate 100 (cat
 * primeste api-ul), oprit la 2000: un om cu mai multe nu-si cauta cererile
 * dintr-o lista.
 */
const ALL_PER_PAGE = 100;
const ALL_MAX_PAGES = 20;

export async function allRequests(scoped?: Scoped): Promise<FeedRow[]> {
  const rows: FeedRow[] = [];

  for (let page = 1; page <= ALL_MAX_PAGES; page += 1) {
    const data = await requests({ page, per_page: ALL_PER_PAGE }, scoped);
    const items = data.items ?? [];

    rows.push(...items);

    if (items.length === 0 || rows.length >= (data.total ?? 0)) break;
  }

  return rows;
}

export function requestDetail(
  id: string | number,
  scoped?: Scoped,
): Promise<RequestDetailResponse> {
  return http.get<RequestDetailResponse>(`/shop/requests/${encodeURIComponent(id)}`, scoped);
}

/*
 * §4 - aprobari: NU exista functii aici, si nu e o omisiune.
 *
 * Rutele `/shop/approvals*` au disparut din contract. Aprobarea e o mutare de
 * stare pe tichet, in platforma, prin fluxul tipului de tichet - deci magazinul
 * n-are ce apela. Sectiunea ramane scrisa ca sa nu para o scapare la urmatoarea
 * citire alaturi de contract.
 */

/*
 * §5 - taskuri: clientul magazinului nu le mai arata.
 *
 * Functiile `requestTasks` / `cancelTask` / `unblockTask` (si `catalogSteps`)
 * stau in `OLD/src/api/shop-tasks.ts`, impreuna cu tabla de taskuri. Rutele
 * raman in api pentru portal; aici nu se mai cheama.
 */

/* ----------------------------------------------------- 8. tichet direct */

export function ticketTypes(scoped?: Scoped): Promise<TicketTypesResponse> {
  return http.get<TicketTypesResponse>('/shop/tickets/types', scoped);
}

/**
 * `searchId` e randul din jurnalul casetei de autoservire, cand omul a cautat
 * ceva inainte si n-a gasit.
 *
 * Cu el, api-ul scrie pe tichet o nota interna cu ce a incercat - si tot el
 * noteaza in jurnal ca s-a ajuns la tichet. Pana acum pagina trimitea masura
 * aia separat, dupa raspuns, iar cine inchidea fila in secunda aia o pierdea.
 * `0` inseamna ca n-a cautat: tichetul se deschide ca oricare altul.
 */
export function createTicket(
  typeId: number,
  subject: string,
  description: string,
  searchId = 0,
): Promise<CreateTicketResponse> {
  return http.post<CreateTicketResponse>('/shop/tickets', {
    body: { type_id: typeId, subject, description, search_id: searchId },
  });
}

/* ------------------------------------------------------- 9. autoservire */

export interface HelpQuery {
  q?: string;
  category_id?: string;
  page?: number;
  per_page?: number;
}

/**
 * Baza de cunostinte rasfoita. NU e cautarea casetei - aia e `search()`.
 *
 * Aici filtrul e un LIKE, ca la catalog: o lista rasfoita care ar sari peste
 * randuri fiindca scorul lor e mic ar arata ca si cum articolele ar disparea.
 */
export function helpList(query: HelpQuery, scoped?: Scoped): Promise<HelpListResponse> {
  return http.get<HelpListResponse>('/shop/help', withQuery(scoped, { ...query }));
}

export function helpCategories(scoped?: Scoped): Promise<CategoriesResponse> {
  return http.get<CategoriesResponse>('/shop/help/categories', scoped);
}

export function helpArticle(id: string | number, scoped?: Scoped): Promise<HelpArticleResponse> {
  return http.get<HelpArticleResponse>(`/shop/help/${encodeURIComponent(id)}`, scoped);
}

/** Ruta casetei: amandoua sursele, in doua liste separate. */
export function search(q: string, lang: string, scoped?: Scoped): Promise<SearchResponse> {
  return http.get<SearchResponse>('/shop/search', withQuery(scoped, { q, lang }));
}

/**
 * Ce a facut omul dupa ce a cautat.
 *
 * NU se asteapta dupa ea si NU se arata daca esueaza: e o masura pentru noi,
 * luata pe drum. Un om caruia i s-ar arata "raportarea a esuat" in timp ce
 * deschide un articol ar crede ca articolul n-a mers.
 *
 * Numai `article` si `cart` mai trec pe aici. `ticket` s-a mutat in api, in
 * `createTicket`: acolo exista un moment in care se stie SIGUR ca s-a ajuns la
 * tichet, iar aici era doar o cerere trimisa in urma, pe care o inghitea orice
 * fila inchisa. Cele doua ramase chiar se intampla numai in browser - nu exista
 * nicio cerere catre server in care sa incapa.
 */
export function searchOutcome(
  searchId: number,
  outcome: SearchOutcome,
  articleId = 0,
): Promise<unknown> {
  return http.post('/shop/search/outcome', {
    /*
     * `article_id` insoteste outcome-ul `article`.
     *
     * Fara el se stia doar CA omul a citit ceva, nu si CE - iar intrebarea care
     * conteaza e care articole sunt citite si urmate de un tichet oricum. Alea
     * sunt articolele de rescris, si fara numarul lor raspunsul e "unul dintre
     * ele".
     */
    body: { search_id: searchId, outcome, article_id: articleId },
  });
}
